/*
 * RebateAgreementUploadModal - Complex file upload modal for rebate agreements
 *
 * UI/UX DESCRIPTION:
 * - Modal dialog with file upload dropzone and form fields
 * - Supplier selection dropdown with search functionality
 * - AI model selection for document processing
 * - Date range picker for agreement period (start/end dates)
 * - File preview with remove functionality
 * - Error handling with extraction failure fallback
 * - Manual creation form when AI extraction fails
 * - Loading states during upload and processing
 *
 * FUNCTIONALITY:
 * - Upload PDF/image files for AI processing
 * - Extract rebate agreement data using AI models
 * - Handle extraction failures gracefully with manual creation option
 * - Validate file types (PDF, JPG, JPEG, PNG)
 * - Single file upload restriction
 * - Date validation (end date after start date)
 * - Form validation with error messages
 * - Progress tracking during upload
 * - Success/error state management
 */

import React, { useState, useEffect } from 'react';
import {
  TrashIcon,
  CircleNotchIcon,
  WarningIcon,
  CalendarIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Card, CardContent } from 'src/components/ui/card';
import { cn } from '@/lib/utils';
import FileIcon from 'src/components/documents/FileIcon';
import SelectDropdown, {
  SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import useAllSuppliers from 'src/api/accounts-payable/useAllSuppliersQuery';
import useCreateRebateAgreement from 'src/api/rebates/useCreateRebateAgreement';
import useDuplicateRebateAgreement from 'src/api/rebates/useDuplicateRebateAgreement';
import { RebateAgreementListItem } from 'src/api/rebates/types';
import PrimaryButton from 'src/components/common/PrimaryButton';
import CancelButton from 'src/components/common/CancelButton';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import { UploadProgress } from 'src/types/UserDocuments';
import {
  AI_MODELS,
  SupportedModelId,
  getModelOption,
} from 'src/types/aiModels';
import AIModelSelector, {
  useModelFallback,
} from 'src/components/common/AIModelSelector';

const MAX_FILES = 3;

interface RebateAgreementUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (agreementId?: string) => void;
  isManualMode?: boolean; // If true, hide file upload and AI model sections
  onSwitchToManualMode?: () => void; // Callback to switch to manual mode
  agreements: RebateAgreementListItem[]; // All agreements for overlap and name checking
  agreementToDuplicate?: RebateAgreementListItem | null; // If provided, pre-fill form for duplication
}

const RebateAgreementUploadModal: React.FC<RebateAgreementUploadModalProps> = ({
  open,
  onClose,
  onSuccess,
  isManualMode = false,
  onSwitchToManualMode,
  agreements,
  agreementToDuplicate,
}) => {
  const [selectedSupplier, setSelectedSupplier] =
    useState<SelectDropdownOption | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<SupportedModelId>(
    AI_MODELS.CLAUDE_OPUS_4_1,
  );
  const [startDate, setStartDate] = useState<string>(() => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-01-01`;
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-12-31`;
  });

  // New state for extraction failure
  const [extractionFailed, setExtractionFailed] = useState(false);
  const [agreementName, setAgreementName] = useState<string>('');

  // State for service error information
  const [_serviceError, setServiceError] = useState<{
    type: 'rate_limit' | 'service_unavailable' | null;
    provider?: string;
    canRetryLater?: boolean;
  }>({ type: null });

  // Local overlap and name conflict checking (no API calls)
  const [overlappingAgreement, setOverlappingAgreement] =
    useState<RebateAgreementListItem | null>(null);
  const [replaceMode, setReplaceMode] = useState(false); // User's choice: false = create alongside, true = replace
  const [finalAgreementName, setFinalAgreementName] = useState<string>(''); // Name with sequence number if needed

  // Use shared model fallback logic
  const { getAlternativeModel } = useModelFallback();

  // Auto-generate agreement name when supplier or dates change
  React.useEffect(() => {
    if (selectedSupplier && startDate && endDate && !agreementToDuplicate) {
      // Parse dates in local timezone to avoid UTC conversion issues
      const startYear = new Date(startDate + 'T00:00:00').getFullYear();
      const endYear = new Date(endDate + 'T00:00:00').getFullYear();
      const yearRange =
        startYear === endYear ? startYear : `${startYear}-${endYear}`;
      const generatedName = `${selectedSupplier.label} - Rebate Agreement ${yearRange}`;
      setAgreementName(generatedName);
    }
  }, [selectedSupplier, startDate, endDate, agreementToDuplicate]);

  // Pre-fill form when duplicating an existing agreement
  React.useEffect(() => {
    if (open && agreementToDuplicate) {
      // Pre-select the supplier
      setSelectedSupplier({
        id: agreementToDuplicate.supplier.id,
        label: agreementToDuplicate.supplier.name,
        value: agreementToDuplicate.supplier.id,
        display: agreementToDuplicate.supplier.name,
      });

      // Calculate dates for next year
      const originalStart = new Date(
        agreementToDuplicate.start_date + 'T00:00:00',
      );
      const originalEnd = new Date(agreementToDuplicate.end_date + 'T00:00:00');

      const nextYearStart = new Date(originalStart);
      nextYearStart.setFullYear(originalStart.getFullYear() + 1);

      const nextYearEnd = new Date(originalEnd);
      nextYearEnd.setFullYear(originalEnd.getFullYear() + 1);

      const nextStartDate = nextYearStart.toISOString().split('T')[0];
      const nextEndDate = nextYearEnd.toISOString().split('T')[0];

      setStartDate(nextStartDate);
      setEndDate(nextEndDate);

      // Generate name for next year
      const startYear = nextYearStart.getFullYear();
      const endYear = nextYearEnd.getFullYear();
      const yearRange =
        startYear === endYear ? startYear : `${startYear}-${endYear}`;
      const duplicatedName = `${agreementToDuplicate.supplier.name} - Rebate Agreement ${yearRange}`;
      setAgreementName(duplicatedName);
    }
  }, [open, agreementToDuplicate]);

  // Handle business logic errors (returned as 200 with success: false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBusinessLogicError = (errorData: any) => {
    const errorType = errorData?.error?.type;
    let message = errorData?.error?.message || 'An error occurred';

    console.log('Handling business logic error:', { errorType, errorData });

    // Helper to add timing info to message
    const addTimingInfo = () => {
      if (errorData.error.resetTime) {
        const resetTime = new Date(errorData.error.resetTime);
        const now = new Date();
        const minutesUntilReset = Math.ceil(
          (resetTime.getTime() - now.getTime()) / (1000 * 60),
        );

        if (minutesUntilReset > 0 && minutesUntilReset < 1440) {
          if (minutesUntilReset < 60) {
            message += ` Rate limit resets in ${minutesUntilReset} minute${
              minutesUntilReset === 1 ? '' : 's'
            }.`;
          } else {
            const hours = Math.ceil(minutesUntilReset / 60);
            message += ` Rate limit resets in ${hours} hour${
              hours === 1 ? '' : 's'
            }.`;
          }
        }
      } else if (errorData.error.retryAfter) {
        const retryAfterMinutes = Math.ceil(errorData.error.retryAfter / 60);
        message += ` Please try again in ${retryAfterMinutes} minute${
          retryAfterMinutes === 1 ? '' : 's'
        }.`;
      }
    };

    // Helper to auto-switch model
    const tryAutoSwitchModel = (reason: string) => {
      console.log('🔄 Attempting to get alternative model for:', selectedModel);
      const suggestedModel = getAlternativeModel(selectedModel);
      console.log('🔄 Alternative model found:', suggestedModel);

      if (suggestedModel) {
        const modelDisplay =
          getModelOption(suggestedModel)?.display || suggestedModel;
        console.log(
          `✅ Auto-switching from ${selectedModel} to ${suggestedModel} due to ${reason}`,
        );
        setSelectedModel(suggestedModel);
        message += ` Automatically switched to ${modelDisplay}. Please try uploading again.`;
      } else {
        console.log('❌ No alternative model found');
      }
    };

    // Handle different error types
    if (errorType === 'rate_limit') {
      console.log('🔴 HANDLING RATE LIMIT ERROR');
      addTimingInfo();
      tryAutoSwitchModel('rate limit');
      setServiceError({
        type: 'rate_limit',
        provider: errorData.error.provider,
        canRetryLater: true,
      });
      setExtractionFailed(true);
    } else if (errorType === 'service_unavailable') {
      console.log('🔴 HANDLING SERVICE UNAVAILABLE ERROR');
      if (errorData.error.canRetryLater) {
        message +=
          ' This is likely temporary - please try again in a few minutes.';
      }
      tryAutoSwitchModel('service unavailability');
      setServiceError({
        type: 'service_unavailable',
        provider: errorData.error.provider,
        canRetryLater: errorData.error.canRetryLater,
      });
      setExtractionFailed(true);
    } else if (errorType === 'extraction_failed') {
      console.log('🔴 HANDLING EXTRACTION FAILED ERROR');
      setExtractionFailed(true);
    } else {
      console.log('🔴 HANDLING GENERIC ERROR');
    }

    setError(message);
  };

  const { data: suppliers, isLoading: suppliersLoading } = useAllSuppliers(
    { module: 'rebates' }, // Show suppliers with rebates module enabled
    open,
  );
  const createRebateAgreement = useCreateRebateAgreement();
  const duplicateRebateAgreement = useDuplicateRebateAgreement();

  // Computed loading state
  const isLoading = agreementToDuplicate
    ? duplicateRebateAgreement.isLoading
    : createRebateAgreement.isLoading;

  // Check for overlapping agreements and calculate final name (all local, no API calls)
  useEffect(() => {
    // Reset when modal closes
    if (!open) {
      setOverlappingAgreement(null);
      setReplaceMode(false);
      setFinalAgreementName('');
      return;
    }

    // Only check if we have all required data
    if (!selectedSupplier?.id || !startDate || !endDate || !agreementName) {
      setOverlappingAgreement(null);
      setReplaceMode(false);
      setFinalAgreementName(agreementName);
      return;
    }

    // Filter agreements for this supplier
    const supplierAgreements = agreements.filter(
      (a) => a.supplier_id === selectedSupplier.id,
    );

    // Check for date overlap: two date ranges overlap if start1 <= end2 AND start2 <= end1
    const overlap = supplierAgreements.find(
      (a) => a.start_date <= endDate && a.end_date >= startDate,
    );

    if (overlap) {
      setOverlappingAgreement(overlap);
      setReplaceMode(true); // Default to replace for safety
    } else {
      setOverlappingAgreement(null);
      setReplaceMode(false);
    }

    // Calculate final name with sequence number if needed (only for create-alongside mode)
    if (!replaceMode) {
      // Check for name conflicts
      const existingWithSameName = supplierAgreements.filter(
        (a) =>
          a.name === agreementName ||
          a.name.match(
            new RegExp(
              `^${agreementName.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&',
              )} \\(\\d+\\)$`,
            ),
          ),
      );

      if (existingWithSameName.length > 0) {
        // Extract sequence numbers
        const sequenceNumbers: number[] = [];
        existingWithSameName.forEach((a) => {
          if (a.name === agreementName) {
            sequenceNumbers.push(1);
          } else {
            const match = a.name.match(/\((\d+)\)$/);
            if (match) {
              sequenceNumbers.push(parseInt(match[1], 10));
            }
          }
        });

        // Use next available sequence number
        const nextSequence = Math.max(...sequenceNumbers) + 1;
        setFinalAgreementName(`${agreementName} (${nextSequence})`);
      } else {
        setFinalAgreementName(agreementName);
      }
    } else {
      // In replace mode, use the name as-is (no sequence needed)
      setFinalAgreementName(agreementName);
    }
  }, [
    selectedSupplier?.id,
    startDate,
    endDate,
    agreementName,
    replaceMode,
    agreements,
    open,
  ]);

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      // Format date as YYYY-MM-DD in local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setStartDate(`${year}-${month}-${day}`);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      // Format date as YYYY-MM-DD in local timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setEndDate(`${year}-${month}-${day}`);
    }
  };

  const handleFilesSelected = (files: File[]) => {
    // Validate file types for rebate agreements
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];

    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type),
    );
    if (invalidFiles.length > 0) {
      setError('Please select only PDF, JPG, JPEG, or PNG files.');
      return;
    }

    // Allow up to MAX_FILES files for rebate agreements
    if (files.length > MAX_FILES) {
      setError(`Please select up to ${MAX_FILES} files maximum.`);
      return;
    }

    setError('');
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    // Unified validation
    if (!selectedSupplier) {
      setError('Please select a supplier.');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after start date.');
      return;
    }

    // Mode-specific validation
    if (agreementToDuplicate) {
      // Duplicating mode - just needs name
      if (!agreementName.trim()) {
        setError('Please enter an agreement name.');
        return;
      }
    } else if (isManualMode) {
      // Manual mode - needs name
      if (!agreementName.trim()) {
        setError('Please enter an agreement name.');
        return;
      }
    } else {
      // AI mode requires files
      if (selectedFiles.length === 0) {
        setError('Please select at least one file to upload.');
        return;
      }
    }

    try {
      let result;

      // If duplicating, use the duplicate endpoint
      if (agreementToDuplicate) {
        result = await duplicateRebateAgreement.mutateAsync({
          sourceAgreementId: agreementToDuplicate.id,
          name: finalAgreementName.trim(),
          startDate,
          endDate,
        });
      } else {
        // Otherwise use the create endpoint
        result = await createRebateAgreement.mutateAsync({
          supplierId: selectedSupplier.id,
          files: selectedFiles.length > 0 ? selectedFiles : undefined,
          startDate,
          endDate,
          model: selectedModel,
          agreementName: finalAgreementName.trim() || undefined, // Use final name with sequence number
          manualMode: isManualMode,
          // Only pass replaceExistingAgreementId if user chose replace mode
          replaceExistingAgreementId:
            replaceMode && overlappingAgreement?.id
              ? overlappingAgreement.id
              : undefined,
        });
      }

      console.log('Mutation result:', result);

      // Check if the response indicates success or failure
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((result?.data as any)?.success === false) {
        console.log('Received business logic error:', result.data);
        handleBusinessLogicError(result.data);
        return;
      }

      // SUCCESS: Agreement created successfully
      // The response structure is: { data: { success: true, data: { id, ... } } }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const agreementData = (result?.data as any)?.data;
      handleSuccess(agreementData?.id || result.data.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isManualMode ? 'create' : 'upload'} rebate agreement`,
      );
    }
  };

  const handleSuccess = (agreementId?: string) => {
    // Reset form and close modal
    setSelectedSupplier(null);
    setSelectedFiles([]);
    setUploadProgress([]);
    setError('');
    setStartDate(() => {
      const currentYear = new Date().getFullYear();
      return `${currentYear}-01-01`;
    });
    setEndDate(() => {
      const currentYear = new Date().getFullYear();
      return `${currentYear}-12-31`;
    });
    setExtractionFailed(false);
    setAgreementName('');
    setServiceError({ type: null });
    onSuccess?.(agreementId);
    onClose();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setSelectedSupplier(null);
    setSelectedFiles([]);
    setUploadProgress([]);
    setError('');
    setStartDate(() => {
      const currentYear = new Date().getFullYear();
      return `${currentYear}-01-01`;
    });
    setEndDate(() => {
      const currentYear = new Date().getFullYear();
      return `${currentYear}-12-31`;
    });
    setExtractionFailed(false);
    setAgreementName('');
    setServiceError({ type: null });
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {agreementToDuplicate
              ? 'Duplicate Rebate Agreement'
              : isManualMode
                ? 'Create Rebate Agreement Manually'
                : 'Upload Rebate Agreement'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {/* Unified Error Display */}
          {error && (
            <AlertContainer variant="destructive">
              <AlertDescription>
                <div className="flex flex-col gap-2">
                  {/* Error Message */}
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex-1 text-sm">{error}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setError('');
                        setServiceError({ type: null });
                        setExtractionFailed(false);
                      }}
                      className="h-auto p-1 text-red-600 hover:bg-red-100 flex-shrink-0"
                    >
                      ×
                    </Button>
                  </div>

                  {/* Action Buttons */}
                  {extractionFailed && !isManualMode && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSwitchToManualMode?.()}
                        className="bg-white text-red-600 hover:bg-gray-100 border-red-200"
                      >
                        Create Manually
                      </Button>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </AlertContainer>
          )}

          <div>
            <SelectDropdown
              label="Supplier"
              placeholder="Select a supplier"
              searchPlaceholder="Search suppliers"
              options={(suppliers || []).map((supplier) => ({
                id: supplier.id,
                display: supplier.name,
                label: supplier.name,
                value: supplier,
              }))}
              value={selectedSupplier}
              onChange={setSelectedSupplier}
              disabled={suppliersLoading}
              required
            />
          </div>

          {selectedSupplier && (
            <div className="space-y-2">
              <Label htmlFor="agreementName" className="text-sm font-medium">
                Agreement Name
              </Label>
              <Input
                id="agreementName"
                type="text"
                value={agreementName}
                onChange={(e) => setAgreementName(e.target.value)}
                placeholder="Auto-generated from supplier and dates"
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500">
                Auto-generated, but you can edit it
              </p>
            </div>
          )}

          {/* restore after demo
           */}
          {/* {!isManualMode && !agreementToDuplicate && (
            <AIModelSelector
              label="AI Model"
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              disabled={isLoading}
              placeholder="Select model..."
              fullWidth={true}
            />
          )} */}

          <div className="space-y-2">
            <Label className="text-sm font-medium">Agreement Period</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground',
                      )}
                      disabled={isLoading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate
                        ? format(new Date(startDate + 'T00:00:00'), 'PPP')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      key={`start-${isManualMode}`}
                      mode="single"
                      selected={
                        startDate
                          ? new Date(startDate + 'T00:00:00')
                          : undefined
                      }
                      defaultMonth={
                        startDate
                          ? new Date(startDate + 'T00:00:00')
                          : new Date()
                      }
                      onSelect={handleStartDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground',
                      )}
                      disabled={isLoading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate
                        ? format(new Date(endDate + 'T00:00:00'), 'PPP')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      key={`end-${isManualMode}`}
                      mode="single"
                      selected={
                        endDate ? new Date(endDate + 'T00:00:00') : undefined
                      }
                      defaultMonth={
                        endDate ? new Date(endDate + 'T00:00:00') : new Date()
                      }
                      onSelect={handleEndDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Overlap Warning */}
          {overlappingAgreement && (
            <AlertContainer className="bg-amber-50 border-amber-300">
              <WarningIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <AlertDescription className="text-amber-900 space-y-2">
                  <div className="font-semibold">
                    An agreement already exists for this supplier and period
                  </div>
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="font-medium">Existing Agreement:</span>{' '}
                      {overlappingAgreement.name}
                    </div>
                    <div>
                      <span className="font-medium">Period:</span>{' '}
                      {format(
                        new Date(overlappingAgreement.start_date + 'T00:00:00'),
                        'MMM d, yyyy',
                      )}{' '}
                      -{' '}
                      {format(
                        new Date(overlappingAgreement.end_date + 'T00:00:00'),
                        'MMM d, yyyy',
                      )}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>{' '}
                      <span
                        className={cn(
                          'capitalize',
                          overlappingAgreement.status === 'finalized'
                            ? 'text-green-700'
                            : 'text-yellow-700',
                        )}
                      >
                        {overlappingAgreement.status}
                      </span>
                    </div>
                  </div>

                  {/* Choice: Replace or Create Alongside */}
                  <div className="mt-4 space-y-3">
                    <div className="font-medium text-sm">
                      What would you like to do?
                    </div>
                    <RadioGroup
                      value={replaceMode ? 'replace' : 'alongside'}
                      onValueChange={(value) =>
                        setReplaceMode(value === 'replace')
                      }
                    >
                      <div className="space-y-3">
                        {/* Option 1: Replace */}
                        <div className="flex items-start space-x-3 p-3 bg-amber-100 rounded-md border border-amber-200">
                          <RadioGroupItem
                            value="replace"
                            id="replace"
                            className="mt-0.5"
                          />
                          <Label
                            htmlFor="replace"
                            className="flex-1 cursor-pointer"
                          >
                            <div className="font-medium">
                              Replace existing agreement
                            </div>
                            <div className="text-xs text-amber-900 mt-1">
                              Permanently delete the existing agreement and all
                              associated data (including any payments and
                              invoices), then create this new one.
                            </div>
                          </Label>
                        </div>

                        {/* Option 2: Create Alongside */}
                        <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                          <RadioGroupItem
                            value="alongside"
                            id="alongside"
                            className="mt-0.5"
                          />
                          <Label
                            htmlFor="alongside"
                            className="flex-1 cursor-pointer"
                          >
                            <div className="font-medium">
                              Create alongside existing agreement
                            </div>
                            <div className="text-xs text-blue-900 mt-1">
                              Keep the existing agreement and create this new
                              one with overlapping dates. Both agreements will
                              be active simultaneously.
                              {!replaceMode &&
                                finalAgreementName !== agreementName && (
                                  <span className="block mt-1 font-medium text-blue-950">
                                    → Will be created as: &ldquo;
                                    {finalAgreementName}
                                    &rdquo;
                                  </span>
                                )}
                            </div>
                          </Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                </AlertDescription>
              </div>
            </AlertContainer>
          )}

          {/* Hide file upload section when duplicating */}
          {!agreementToDuplicate && (
            <>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">
                  {isManualMode
                    ? `Upload agreement documents (optional, up to ${MAX_FILES} files)`
                    : `Upload up to ${MAX_FILES} files (PDF, JPG, JPEG, or PNG)`}
                </p>
                <FileUploadDropzone
                  onFilesSelected={handleFilesSelected}
                  uploadProgress={uploadProgress}
                  isUploading={isLoading}
                  maxFiles={MAX_FILES}
                  disabled={isLoading}
                />
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Selected File{selectedFiles.length > 1 ? 's' : ''}
                  </Label>
                  {selectedFiles.map((file, index) => (
                    <Card key={index}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileIcon
                              fileName={file.name}
                              size="small"
                              showBackground={false}
                            />
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFile(index)}
                            disabled={isLoading}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      !selectedSupplier ||
                      !agreementName.trim() ||
                      (agreementToDuplicate
                        ? false
                        : isManualMode
                          ? false
                          : selectedFiles.length === 0) ||
                      !startDate ||
                      !endDate
                    }
                  >
                    {isLoading ? (
                      <>
                        <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                        {agreementToDuplicate
                          ? 'Duplicating...'
                          : overlappingAgreement && replaceMode
                            ? 'Replacing...'
                            : isManualMode
                              ? 'Creating...'
                              : 'Uploading...'}
                      </>
                    ) : agreementToDuplicate ? (
                      'Duplicate Agreement'
                    ) : overlappingAgreement && replaceMode ? (
                      'Replace Existing Agreement'
                    ) : isManualMode ? (
                      'Create Agreement'
                    ) : (
                      'Upload'
                    )}
                  </PrimaryButton>
                </div>
              </TooltipTrigger>
              {(isLoading ||
                !selectedSupplier ||
                !agreementName.trim() ||
                (agreementToDuplicate
                  ? false
                  : isManualMode
                    ? false
                    : selectedFiles.length === 0) ||
                !startDate ||
                !endDate) && (
                <TooltipContent side="bottom">
                  <div className="text-sm">
                    {!selectedSupplier && <p>• Select a supplier</p>}
                    {!agreementName.trim() && <p>• Enter an agreement name</p>}
                    {!agreementToDuplicate &&
                      !isManualMode &&
                      selectedFiles.length === 0 && (
                        <p>• Upload at least one file</p>
                      )}
                    {!startDate && <p>• Select a start date</p>}
                    {!endDate && <p>• Select an end date</p>}
                    {isLoading && <p>• Processing...</p>}
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RebateAgreementUploadModal;
